import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridPagination } from '@mui/x-data-grid';

import { AdAssetViewItem, AdGroupListItem, getAdAssetView } from '~/services/PublicOfferFeedService';

import AIGeneratedSuggestionsModal from './AIGeneratedSuggestionsModal';

type AdAssetViewItemWithId = AdAssetViewItem & { id: string };

function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

type FieldType = 'HEADLINE' | 'DESCRIPTION';

export default function AdCreativeAIAssistant({
  campaignId,
  adGroupId,
  customerId,
  adGroup,
  fieldType = 'HEADLINE',
}: {
  campaignId: string;
  adGroupId: string;
  customerId: string;
  adGroup?: AdGroupListItem;
  fieldType?: FieldType;
}) {
  const [adAssetViewItems, setAdAssetViewItems] = useState<Array<AdAssetViewItemWithId>>([]);
  const [fetchingState, setFetchingState] = useState<Utils.FetchingState>('idle');
  const [aiSelections, setAiSelections] = useState<Set<string>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItemsForGeneration, setSelectedItemsForGeneration] = useState<Array<AdAssetViewItemWithId>>([]);
  const { enqueueSnackbar } = useSnackbar();

  // Filter assets by fieldType
  const filteredAdAssetViewItems = useMemo(
    () => adAssetViewItems.filter((item) => item.fieldType === fieldType),
    [adAssetViewItems, fieldType],
  );

  const fetchAdAssetView = useCallback(
    async (customerId: string, campaignId: string, adGroupId: string) => {
      try {
        setFetchingState('loading');
        const response = await getAdAssetView(customerId, campaignId, adGroupId);
        const itemsWithId = (response.result || []).map(
          (item): AdAssetViewItemWithId => ({
            ...item,
            id: `${item.adGroupAdId}-${item.assetId}`,
          }),
        );
        setAdAssetViewItems(itemsWithId);
        setFetchingState('success');
      } catch (error) {
        setFetchingState('failed');
        enqueueSnackbar('Failed to fetch ad asset view data', { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    if (campaignId && adGroupId && customerId) {
      fetchAdAssetView(customerId, campaignId, adGroupId);
    }
  }, [customerId, campaignId, adGroupId, fetchAdAssetView]);

  // Clear selections when fieldType changes
  useEffect(() => {
    setAiSelections(new Set());
  }, [fieldType]);

  const columns: Array<GridColDef> = [
    {
      field: 'adGroupAdId',
      headerName: 'Ad Id',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'fieldType',
      headerName: 'Asset Type',
      headerAlign: 'center',
      flex: 1,
      align: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value === 'HEADLINE' ? 'Headline' : 'Description'}
          color={params.value === 'HEADLINE' ? 'primary' : 'secondary'}
          size="small"
        />
      ),
    },
    {
      field: 'textAsset',
      headerName: 'Copy',
      flex: 4,
      renderCell: (params) => (
        <Stack spacing={0.5}>
          <Typography variant="body2">{params.value}</Typography>
          <Typography variant="caption" color="text.secondary">
            Impressions {params.row.impressions} • Clicks {params.row.clicks} • Average CPC {params.row.averageCpc} •
            Conversions {params.row.allConversions}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'relativePerformance',
      headerName: 'Performance vs. Group',
      flex: 2,
      headerAlign: 'right',
      align: 'right',
      renderHeader: () => (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ width: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Performance vs. Group
          </Typography>
          <Tooltip
            title="Compares this asset's CTR to the ad group's average CTR. A score of 100% means it performs at the group average. Scores above 100% indicate better performance, while scores below 100% indicate worse performance."
            arrow
          >
            <InfoOutlinedIcon fontSize="small" sx={{ cursor: 'help', color: 'text.secondary' }} />
          </Tooltip>
        </Stack>
      ),
      renderCell: (params) => {
        const rowCtr = params.row?.ctr ?? 0;
        const groupCtr = adGroup?.ctr ?? 0;
        const score = groupCtr > 0 ? (rowCtr * 100) / groupCtr : 0;
        const displayScore = Number.isNaN(score) ? 'N/A' : `${score.toFixed(0)} %`;

        return (
          <Stack spacing={0.5} alignItems="flex-end">
            <Typography color={score > 105 ? 'success.main' : score < 90 ? 'error.main' : 'text.primary'}>
              Score {displayScore}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              CTR {rowCtr.toFixed(2)}%
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'impressions',
      headerName: 'Impressions',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => Number(value || 0).toLocaleString(),
    },
    {
      field: 'allConversions',
      headerName: 'TTV',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => Number(value).toLocaleString(),
    },
    {
      field: 'cost',
      headerName: 'Spend',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => `$${Number(value || 0).toLocaleString()}`,
    },
    {
      field: 'ctr',
      headerName: 'CTR',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => formatPercent(value || 0),
    },
    {
      field: 'ai',
      headerName: 'Replace with AI',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          color="primary"
          checked={aiSelections.has(params.row.id)}
          onChange={(event) => {
            const updated = new Set(aiSelections);
            if (event.target.checked) {
              updated.add(params.row.id);
            } else {
              updated.delete(params.row.id);
            }
            setAiSelections(updated);
          }}
          inputProps={{ 'aria-label': `Mark ${params.row.text} for AI replacement` }}
        />
      ),
    },
  ];

  const handleGenerate = () => {
    if (aiSelections.size === 0) {
      enqueueSnackbar('Please select at least one asset to generate AI replacements', { variant: 'warning' });
      return;
    }

    const selectedItems = filteredAdAssetViewItems.filter((item) => aiSelections.has(item.id));
    setSelectedItemsForGeneration(selectedItems);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedItemsForGeneration([]);
  };

  const handleSaveSuccess = () => {
    if (campaignId && adGroupId && customerId) {
      fetchAdAssetView(customerId, campaignId, adGroupId);
    }
    setAiSelections(new Set());
  };

  if (!customerId || !campaignId || !adGroupId) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Please select a customer, campaign, and ad group to view and manage ad creative assets.
      </Alert>
    );
  }

  if (fetchingState === 'loading') {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress variant="indeterminate" />
      </Box>
    );
  }

  return (
    <Paper>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" color="text.secondary">
              Campaign:
            </Typography>

            <Chip
              key={campaignId}
              label={`${adAssetViewItems?.length > 0 ? adAssetViewItems[0]?.campaignName : ''}`}
              color="primary"
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" color="text.secondary">
              Ad Group:
            </Typography>

            <Chip
              key={campaignId}
              label={`${adAssetViewItems?.length > 0 ? adAssetViewItems[0]?.adGroupName : ''}`}
              color="secondary"
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Typography variant="subtitle2" color="text.secondary">
              Marked for AI:
            </Typography>
            <Chip color="primary" label={aiSelections.size} />
          </Stack>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Impressions
              </Typography>
              <Typography variant="h5">
                {adGroup?.impressions ? Number(adGroup.impressions).toLocaleString() : '0'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total views
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Clicks
              </Typography>
              <Typography variant="h5">{adGroup?.clicks ? Number(adGroup?.clicks).toLocaleString() : '0'}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total Clicks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                CTR
              </Typography>
              <Typography variant="h5">{adGroup?.ctr ? formatPercent(Number(adGroup?.ctr)) : 'N/A'}</Typography>
              <Typography variant="caption" color="text.secondary">
                Across selected ad group
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Spend
              </Typography>
              <Typography variant="h5">${adGroup?.spend ? Number(adGroup?.spend).toLocaleString() : '0'}</Typography>
              <Typography variant="caption" color="text.secondary">
                Combined outlay
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select campaigns to pull every headline and description for their ad groups. Assets are ranked against peers
            in the same ad group using CTR, spend, and total transaction value (TTV). Toggle any rows you want AI to
            replace and generate new copy based on the best performers. Metrics shown for last 2 weeks.
          </Alert>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} textAlign="right">
          <Button variant="contained" color="primary" onClick={handleGenerate} sx={{ mr: 1 }}>
            Generate AI Replacements
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => setAiSelections(new Set())}>
            Clear Selections
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <DataGrid
          rows={filteredAdAssetViewItems}
          getRowId={(row) => row.id}
          columns={columns}
          autoHeight={false}
          checkboxSelection={false}
          density="comfortable"
          disableRowSelectionOnClick
          slots={{ pagination: GridPagination }}
          paginationMode="client"
        />
      </Box>
      <AIGeneratedSuggestionsModal
        open={isModalOpen}
        onClose={handleModalClose}
        items={selectedItemsForGeneration}
        customerId={customerId}
        campaignId={campaignId}
        adGroupId={adGroupId}
        onSaveSuccess={handleSaveSuccess}
      />
    </Paper>
  );
}
